const getAtcCartElementForModal = () => {
  const addType =
    document.documentElement?.dataset?.cartAddType || document.documentElement?.dataset?.cartType;
  const fallback =
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer') ||
    window.cartDrawerElement;

  if (!addType) return fallback;
  if (addType === 'notification') return document.querySelector('cart-notification') || fallback;
  if (addType === 'drawer') return document.querySelector('cart-drawer') || window.cartDrawerElement || fallback;
  return fallback;
};

const CART_CHANGE_SECTION_ID_CACHE = new Map();
const CART_CHANGE_KNOWN_SECTION_IDS = new Set();

if (!customElements.get('cart-change-modal')) {
  customElements.define(
    'cart-change-modal',
    class CartChangeModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.setupIntentPrefetch();

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (this.moved) return;
        this.removeDuplicateModalInstance();
        super.connectedCallback();
      }

      removeDuplicateModalInstance() {
        if (!this.id) return;

        const escapedId =
          window.CSS && typeof window.CSS.escape === 'function'
            ? window.CSS.escape(this.id)
            : this.id.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
        const selector = `cart-change-modal#${escapedId}`;
        const duplicate = Array.from(document.querySelectorAll(selector)).find((modal) => modal !== this);
        if (duplicate) duplicate.remove();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        this.syncStateFromOpener(opener);
        const primaryProductUrl = this.dataset.cartChangeProductUrl || opener.getAttribute('data-product-url');
        const productUrlCandidates = primaryProductUrl ? [primaryProductUrl] : [];

        this.quickAddSectionIds = this.resolveQuickAddSectionIds();
        this.showQuantity = this.dataset.quickAddShowQuantity === 'true';

        super.show(opener);
        this.setLoadingState(true);
        this.renderLoadingShell();

        this.fetchProductElementFromCandidates(productUrlCandidates)
          .then((productElement) => {
            this.preprocessHTML(productElement);
            this.modalContent.innerHTML = '';
            this.modalContent.appendChild(productElement);
            this.setLoadingState(false);
            this.setupInfoScrollIndicator(productElement);

            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .catch((error) => {
            console.error(error);
            this.modalContent.innerHTML = `<p class="quick-add-loading__error">${
              window.cartStrings?.error || 'Something went wrong.'
            }</p>`;
            this.setLoadingState(false);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      syncStateFromOpener(opener) {
        if (!opener?.dataset) return;

        const dataKeys = [
          'cartChangeContext',
          'cartChangeLine',
          'cartChangeKey',
          'cartChangeVariantId',
          'cartChangeQuantity',
          'cartChangeProductUrl',
          'cartChangeSubmitText',
          'cartChangeAddText',
        ];

        dataKeys.forEach((key) => {
          const value = opener.dataset[key];
          if (value === undefined || value === null || value === '') return;
          this.dataset[key] = value;
        });
      }

      async fetchProductElementFromCandidates(productUrlCandidates) {
        let lastError = null;

        for (const productUrl of productUrlCandidates) {
          try {
            return await this.fetchProductElement(productUrl);
          } catch (error) {
            lastError = error;
          }
        }

        throw lastError || new Error('Cart change product URL missing');
      }

      hide(preventFocus = false) {
        const cartElement = getAtcCartElementForModal();
        if (cartElement?.setActiveElement) cartElement.setActiveElement(this.openedBy);

        this.setLoadingState(false);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      setLoadingState(isLoading) {
        this.modalContent.classList.toggle('quick-add-modal__content-info--loading', isLoading);
      }

      renderLoadingShell() {
        this.modalContent.innerHTML = `
          <div class="cart-change-loading" aria-hidden="true">
            <div class="cart-change-loading__line cart-change-loading__line--title"></div>
            <div class="cart-change-loading__line"></div>
            <div class="cart-change-loading__line cart-change-loading__line--short"></div>
            <div class="cart-change-loading__button"></div>
          </div>
        `;
      }

      resolveQuickAddSectionIds() {
        const explicitIds = (this.dataset.quickAddSectionIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        if (explicitIds.length) return explicitIds;
        return ['cart-change-modal-product'];
      }

      isUsableProductElement(productElement) {
        if (!productElement) return false;

        const infoWrapper = productElement.querySelector('.product__info-wrapper');
        const hasInfoWrapper = !!infoWrapper;
        const hasInfoContent =
          !!infoWrapper && (infoWrapper.children.length > 0 || infoWrapper.textContent.trim().length > 0);
        const hasForm = !!productElement.querySelector('product-form form');

        return hasInfoWrapper && hasInfoContent && hasForm;
      }

      fetchProductElement(productUrl) {
        if (!productUrl) return Promise.reject(new Error('Cart change product URL missing'));

        const modalFetcher = window.ModalProductFetch;
        if (!modalFetcher || typeof modalFetcher.fetchProductElement !== 'function') {
          return Promise.reject(new Error('ModalProductFetch helper is not available'));
        }

        const normalizedProductUrl = modalFetcher.normalizeProductUrl(productUrl);
        const productPath = new URL(normalizedProductUrl, window.location.origin).pathname;
        const cachedSectionId = CART_CHANGE_SECTION_ID_CACHE.get(productPath);
        const requestConfigBase = {
          productUrl: normalizedProductUrl,
          validateProductElement: this.isUsableProductElement.bind(this),
        };
        const cacheDiscoveredSectionId = (productElement) => {
          const discoveredSectionId = productElement?.dataset?.section;
          if (discoveredSectionId) {
            CART_CHANGE_SECTION_ID_CACHE.set(productPath, discoveredSectionId);
            CART_CHANGE_KNOWN_SECTION_IDS.add(discoveredSectionId);
          }
          return productElement;
        };
        const fetchFromSections = (sectionIds) => {
          const dedupedSectionIds = [...new Set((sectionIds || []).filter(Boolean))];
          if (!dedupedSectionIds.length) return Promise.reject(new Error('Cart change section IDs missing'));

          return modalFetcher.fetchProductElement({
            ...requestConfigBase,
            fetchMode: 'section',
            sectionIds: dedupedSectionIds,
            includeProductPage: false,
          });
        };
        const fetchFromProductPage = () =>
          modalFetcher.fetchProductElement({
            ...requestConfigBase,
            fetchMode: 'full',
            includeProductPage: true,
          });
        const sectionIdsToTry = [
          cachedSectionId,
          ...this.quickAddSectionIds,
          ...CART_CHANGE_KNOWN_SECTION_IDS,
        ].filter(Boolean);

        return fetchFromSections(sectionIdsToTry)
          .catch(() => fetchFromProductPage())
          .then(cacheDiscoveredSectionId);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });

        productElement.dataset.skipVariantMediaPrune = 'true';
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.applyMinimalLayout(productElement);
        this.removeNonEssentialBlocks(productElement);
        this.keepOnlyCoreInfoBlocks(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.applyCartChangeConfig(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      applyCartChangeConfig(productElement) {
        this.applyMediaVisibility(productElement);

        const productForm = productElement.querySelector('product-form');
        if (!productForm) return;

        productForm.dataset.cartChange = 'true';
        productForm.dataset.cartChangeLine = this.dataset.cartChangeLine || '';
        productForm.dataset.cartChangeKey = this.dataset.cartChangeKey || '';
        productForm.dataset.cartChangeVariantId = this.dataset.cartChangeVariantId || '';
        productForm.dataset.cartChangeQuantity = this.dataset.cartChangeQuantity || '';
        productForm.dataset.cartChangeContext = this.dataset.cartChangeContext || '';
        productForm.dataset.cartChangeSubmitText = this.dataset.cartChangeSubmitText || '';
        productForm.dataset.cartChangeAddText = this.dataset.cartChangeAddText || '';

        const form = productForm.querySelector('form');
        const quantity = parseInt(this.dataset.cartChangeQuantity || '1', 10);

        if (form) {
          let qtyInput = form.querySelector('input[name="quantity"]');
          if (!qtyInput) {
            qtyInput = document.createElement('input');
            qtyInput.type = 'hidden';
            qtyInput.name = 'quantity';
            form.appendChild(qtyInput);
          }

          if (!Number.isNaN(quantity)) {
            qtyInput.value = quantity;
          }
        }

        const submitButton = productForm.querySelector('button[type="submit"]');
        if (!submitButton) return;

        submitButton.dataset.cartChangeAction = 'update';
        const submitText = this.dataset.cartChangeSubmitText;
        if (submitText) {
          const label = submitButton.querySelector('span');
          if (label) {
            label.textContent = submitText;
          } else {
            submitButton.textContent = submitText;
          }
        }

        const buttonsWrapper = submitButton.closest('.product-form__buttons');
        const hasAddButton = buttonsWrapper?.querySelector('button[data-cart-change-action="add"]');
        if (!buttonsWrapper || hasAddButton) return;

        const addButton = submitButton.cloneNode(true);
        addButton.dataset.cartChangeAction = 'add';
        addButton.classList.remove('button--primary');
        addButton.classList.add('button--secondary');
        addButton.removeAttribute('id');
        addButton.removeAttribute('aria-disabled');
        addButton.disabled = submitButton.disabled;

        const addLabel = addButton.querySelector('span');
        const addText = this.dataset.cartChangeAddText || window.variantStrings?.addToCart || 'Add to cart';
        if (addLabel) {
          addLabel.textContent = addText;
        } else {
          addButton.textContent = addText;
        }

        const soldOutMessage = addButton.querySelector('.sold-out-message');
        if (soldOutMessage) soldOutMessage.remove();

        buttonsWrapper.appendChild(addButton);
      }

      applyMediaVisibility(productElement) {
        const productRoot = productElement.querySelector('.product');
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        const showMediaAttr = (this.dataset.quickAddShowMedia || '').trim().toLowerCase();
        const showMedia = !['false', '0', 'no'].includes(showMediaAttr);

        if (showMedia && mediaWrapper && productRoot) {
          productRoot.classList.remove('product--no-media');
          productRoot.classList.add('product--cart-change-media');
          return;
        }

        if (!showMedia && mediaWrapper) {
          mediaWrapper.remove();
        }

        if (productRoot) {
          productRoot.classList.remove('product--cart-change-media');
          productRoot.classList.add('product--no-media');
        }
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const oldId = sectionId;
        const newId = `cartchange-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      applyMinimalLayout(productElement) {
        const minimal = this.dataset.quickAddMinimal === 'true';
        const showMedia = this.dataset.quickAddShowMedia === 'true';
        const compactMedia = this.dataset.quickAddCompactMedia === 'true';
        const compactMediaDesktop = this.dataset.quickAddCompactMediaDesktop === 'true';
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        const shouldCompactMedia = isDesktop ? compactMediaDesktop : compactMedia;

        productElement.classList.toggle('quick-add--compact-media', shouldCompactMedia);
        productElement.classList.toggle('quick-add--compact-media-desktop', compactMediaDesktop && isDesktop);
        productElement.classList.toggle('quick-add--compact-media-desktop-off', isDesktop && !compactMediaDesktop);

        if (!minimal) return;

        productElement.classList.add('quick-add--minimal');

        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (mediaWrapper) {
          if (!showMedia) {
            mediaWrapper.remove();
          } else if (shouldCompactMedia) {
            const gallery = mediaWrapper.querySelector('media-gallery');
            if (gallery) {
              const images = Array.from(gallery.querySelectorAll('li[data-media-id] img'));
              if (images.length) {
                const simpleGallery = document.createElement('div');
                simpleGallery.classList.add('quick-add-minimal-media');
                images.forEach((img) => {
                  const clone = img.cloneNode(true);
                  clone.removeAttribute('loading');
                  simpleGallery.appendChild(clone);
                });
                mediaWrapper.innerHTML = '';
                mediaWrapper.appendChild(simpleGallery);
              }
            }
          }
        }

        if (!this.showQuantity) {
          productElement.querySelectorAll('.product-form__quantity, quantity-input').forEach((el) => el.remove());
        }
      }

      removeNonEssentialBlocks(productElement) {
        const selectorsToRemove = [
          '.product__accordion',
          '.product__badges',
          '.product-badges',
          '.price__badge-popup-opener',
          '.icon-with-text',
          '.price__badges',
          '.product-popup-modal__opener',
          '.product-popup-modal__content',
          '.quantity-note-opener',
          '.quantity-note-trigger',
          '.quick-add-hidden',
          '.complementary-products',
          '.product__description',
          '.product__tax',
          '.share-button',
          '.shopify-payment-button',
          'modal-dialog',
          'product-modal',
        ];

        selectorsToRemove.forEach((selector) => {
          productElement.querySelectorAll(selector).forEach((element) => element.remove());
        });
      }

      keepOnlyCoreInfoBlocks(productElement) {
        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) return;

        const allowedSelectors = [
          '.product__title',
          '[id^="price-"]',
          '.volume-pricing-note',
          'variant-selects',
          '.product__inventory',
          '.product-form__quantity',
          'product-form',
          '.product-form__buttons',
          '.product-form__error-message-wrapper',
        ];

        Array.from(infoContainer.children).forEach((child) => {
          if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') return;
          if (
            allowedSelectors.some(
              (selector) => child.matches(selector) || (typeof child.querySelector === 'function' && child.querySelector(selector))
            )
          ) {
            return;
          }
          child.remove();
        });
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      setupInfoScrollIndicator(root) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const infoWrapper = root.querySelector('.product__info-wrapper.grid__item');
        if (!infoWrapper || infoWrapper.dataset.scrollIndicatorReady) return;

        infoWrapper.dataset.scrollIndicatorReady = 'true';

        const indicator = document.createElement('span');
        indicator.className = 'quick-add-scroll-indicator';
        infoWrapper.appendChild(indicator);

        const update = () => {
          const scrollHeight = infoWrapper.scrollHeight;
          const clientHeight = infoWrapper.clientHeight;
          const isScrollable = scrollHeight > clientHeight + 1;
          infoWrapper.dataset.scrollable = isScrollable ? 'true' : 'false';

          if (!isScrollable) {
            indicator.style.setProperty('--scroll-thumb-height', '0px');
            indicator.style.setProperty('--scroll-thumb-top', '0px');
            return;
          }

          const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * clientHeight);
          const maxTop = Math.max(0, clientHeight - thumbHeight);
          const top =
            scrollHeight === clientHeight
              ? 0
              : (infoWrapper.scrollTop / (scrollHeight - clientHeight)) * maxTop;

          indicator.style.setProperty('--scroll-thumb-height', `${thumbHeight}px`);
          indicator.style.setProperty('--scroll-thumb-top', `${top}px`);
        };

        infoWrapper.addEventListener('scroll', update, { passive: true });

        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(update);
          observer.observe(infoWrapper);
        } else {
          window.addEventListener('resize', update);
        }

        requestAnimationFrame(update);
      }

      prefetchFromTrigger(trigger) {
        const productUrl = this.dataset.cartChangeProductUrl || trigger?.getAttribute?.('data-product-url');
        if (!productUrl) return;

        const modalFetcher = window.ModalProductFetch;
        if (!modalFetcher?.prefetchProductData) return;

        const normalizedProductUrl = modalFetcher.normalizeProductUrl(productUrl);
        const productPath = new URL(normalizedProductUrl, window.location.origin).pathname;
        const cachedSectionId = CART_CHANGE_SECTION_ID_CACHE.get(productPath);
        const sectionIds = [
          cachedSectionId,
          ...this.resolveQuickAddSectionIds(),
          ...CART_CHANGE_KNOWN_SECTION_IDS,
        ].filter(Boolean);
        const validateProductElement = this.isUsableProductElement.bind(this);

        if (!sectionIds.length) return;

        modalFetcher
          .prefetchProductData({
            productUrl: normalizedProductUrl,
            fetchMode: 'section',
            sectionIds,
            validateProductElement,
            includeProductPage: false,
          })
          .then((found) => {
            if (found) return;
            return modalFetcher.prefetchProductData({
              productUrl: normalizedProductUrl,
              fetchMode: 'full',
              validateProductElement,
              includeProductPage: true,
            });
          })
          .catch(() => {});
      }

      setupIntentPrefetch() {
        const CartChangeModalConstructor = this.constructor;
        if (CartChangeModalConstructor.intentPrefetchBound) return;
        CartChangeModalConstructor.intentPrefetchBound = true;

        const intentHandler = (event) => {
          const trigger = event.target?.closest?.('modal-opener button[data-product-url]');
          if (!trigger) return;

          const modalOpener = trigger.closest('modal-opener');
          const modalTarget = modalOpener?.getAttribute('data-modal');
          if (!modalTarget || modalTarget.charAt(0) !== '#') return;

          const modal = document.querySelector(modalTarget);
          if (!modal || modal.tagName !== 'CART-CHANGE-MODAL') return;
          if (typeof modal.prefetchFromTrigger !== 'function') return;

          modal.prefetchFromTrigger(trigger);
        };

        document.addEventListener('mouseover', intentHandler, { passive: true });
        document.addEventListener('focusin', intentHandler);
        document.addEventListener('touchstart', intentHandler, { passive: true });
      }
    }
  );
}
